import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Modal, Field, SaButton, sa, errMsg } from './ui';
import { SettingsEditor } from './SettingsEditor';
import { appUrl, getSite } from '../../lib/site';

const STEPS = ['Company', 'Branding & Contact', 'Pricing, Tax & Terms', 'Integrations', 'Company Admin', 'Review & Activate'];
const STEP_SECTIONS = [[], ['profile', 'branding', 'domains'], ['pricing', 'tax', 'documents', 'features'], ['email', 'payments', 'suppliers', 'shipping'], [], []];

export const OnboardingWizard = ({ onClose, onDone, companies }) => {
  const [step, setStep] = useState(0);
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', copy_pricing_from: '' });
  const [admin, setAdmin] = useState({ email: '', name: '', password: '' });
  const [adminDone, setAdminDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = company ? `/superadmin/companies/${company.id}/settings` : '';

  const createCompany = async () => {
    if (!form.name.trim()) return toast.error('Company name is required');
    setBusy(true);
    try {
      const { data } = await axios.post('/superadmin/companies', { name: form.name, slug: form.slug || undefined, status: 'onboarding' });
      const c = data.company;
      if (form.copy_pricing_from) await axios.post(`/superadmin/companies/${c.id}/settings/copy-pricing-from/${form.copy_pricing_from}`).catch(() => toast.error('Pricing copy failed'));
      const s = await axios.get(`/superadmin/companies/${c.id}/settings`);
      setCompany(c); setSettings(s.data.settings); setStep(1);
    } catch (e) { toast.error(errMsg(e, 'Could not create company')); } finally { setBusy(false); }
  };

  const createAdmin = async () => {
    if (!admin.email || (admin.password || '').length < 8) return toast.error('Admin email and a password of at least 8 characters are required');
    setBusy(true);
    try {
      const { data } = await axios.post(`/superadmin/companies/${company.id}/admins`, admin);
      setAdminDone(data); toast.success('Company admin created'); setStep(5);
    } catch (e) { toast.error(errMsg(e, 'Could not create admin')); } finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true);
    try { await axios.post(`/superadmin/companies/${company.id}/activate`); toast.success(`${company.name} activated`); onDone(); }
    catch (e) { toast.error(errMsg(e, 'Activation failed')); } finally { setBusy(false); }
  };

  return (
    <Modal title="New Print Shop Onboarding" onClose={onClose} width={1100} testId="onboarding-wizard">
      <ol style={{ display: 'flex', gap: 6, listStyle: 'none', padding: 0, margin: '0 0 20px', flexWrap: 'wrap' }}>
        {STEPS.map((s, i) => (
          <li key={s} data-testid={`wizard-step-${i}`} style={{ ...sa.overline, padding: '6px 10px', color: i === step ? '#fff' : i < step ? '#4ade80' : '#52525b', borderBottom: i === step ? '2px solid #D90000' : '2px solid transparent' }}>{i + 1}. {s}</li>
        ))}
      </ol>
      {step === 0 && (
        <div style={{ maxWidth: 560 }}>
          <Field label="Company name"><input data-testid="wizard-company-name" style={sa.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></Field>
          <Field label="Slug (optional)"><input data-testid="wizard-company-slug" style={sa.input} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></Field>
          <Field label="Start pricing matrices from">
            <select data-testid="wizard-copy-pricing" style={sa.input} value={form.copy_pricing_from} onChange={(e) => setForm({ ...form, copy_pricing_from: e.target.value })}>
              <option value="">Platform defaults</option>
              {companies.map((c) => <option key={c.id} value={c.id}>Copy from {c.name}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><SaButton data-testid="wizard-create-btn" onClick={createCompany} disabled={busy}>Create & continue</SaButton></div>
        </div>
      )}
      {step >= 1 && step <= 3 && settings && (
        <>
          <SettingsEditor baseUrl={base} logoUrl={`${base}/logo`} sections={STEP_SECTIONS[step]} initial={settings} companyId={company.id} onSaved={(r) => setSettings(r.settings)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16, borderTop: '1px solid #27272A', paddingTop: 12 }}>
            <SaButton variant="ghost" onClick={() => setStep(step - 1)} disabled={step === 1}>Back</SaButton>
            <SaButton data-testid="wizard-next-btn" onClick={() => setStep(step + 1)}>Next</SaButton>
          </div>
        </>
      )}
      {step === 4 && (
        <div style={{ maxWidth: 560 }}>
          <Field label="Admin email"><input data-testid="wizard-admin-email" style={sa.input} value={admin.email} onChange={(e) => setAdmin({ ...admin, email: e.target.value })} /></Field>
          <Field label="Admin name"><input data-testid="wizard-admin-name" style={sa.input} value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} /></Field>
          <Field label="Temporary password (min 8 chars)"><input data-testid="wizard-admin-password" style={sa.input} value={admin.password} onChange={(e) => setAdmin({ ...admin, password: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SaButton variant="ghost" onClick={() => setStep(3)}>Back</SaButton>
            <SaButton data-testid="wizard-create-admin-btn" onClick={createAdmin} disabled={busy}>Create admin & continue</SaButton>
          </div>
        </div>
      )}
      {step === 5 && (
        <div style={{ maxWidth: 640 }} data-testid="wizard-review">
          <p style={{ fontSize: 15 }}><strong>{company.name}</strong> (/{company.slug}) is configured and ready.</p>
          <ul style={{ color: '#A1A1AA', fontSize: 14, lineHeight: 1.8 }}>
            <li>Company admin: {adminDone?.email}</li>
            <li>Staff login: <span data-testid="wizard-staff-login-url">{appUrl()}/login</span></li>
            <li>Storefront: <span data-testid="wizard-storefront-url">{settings?.domains?.custom_domain ? `https://${settings.domains.custom_domain}` : (getSite().platform?.domain ? `https://${settings?.domains?.subdomain || company.slug}.${getSite().platform.domain}` : `${window.location.origin}/?company=${company.slug}`)}</span></li>
            <li>Status: onboarding → will become <strong style={{ color: '#4ade80' }}>active</strong> (logins allowed) when you activate.</li>
          </ul>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <SaButton variant="ghost" onClick={onDone}>Finish later (keep onboarding)</SaButton>
            <SaButton data-testid="wizard-activate-btn" onClick={activate} disabled={busy}>Activate company</SaButton>
          </div>
        </div>
      )}
    </Modal>
  );
};
